import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";
import { ArrowUp, Loader2, Paperclip, Square } from "lucide-react";

import { createCarrierLogoImg } from "@/components/common/CarrierLogo";
import { useTheme } from "@/context/ThemeContext";
import { useUser } from "@/auth/UserContext";
import { isClientAllowedEntityTagKind } from "@/features/kai/kaiClientPortal";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { glassMenuPanelClassName } from "@/lib/glassMenu";
import { cn } from "@/lib/utils";
import { MOBILE_SHELL_MQ } from "@/lib/viewport";
import { KAI_ACCEPT } from "@/lib/kaiAttachments";
import { Button } from "@/components/ui/button";
import type { AttachmentEntry } from "@/hooks/useKaiAttachments";
import {
  entityTagKindsForRole,
  KAI_PORT_ROLE_OPTIONS,
  entityPillClassName,
  entityTagKindDef,
  entityTagSubtitle,
  searchAllEntityTags,
  searchEntityTags,
  type KaiEntityTag,
  type KaiEntityTagKind,
  type KaiPortRole,
} from "./kaiEntityTags";
import {
  detectMentionQuery,
  dropdownAllowsSpace,
  mentionStillExited,
  shouldKeepMentionOnSpace,
} from "./kaiMentionQuery";
import { detectSlashQuery, filterSlashCommands } from "./kaiSlashQuery";
import { groupSlashCommands, slashPillClassName } from "./kaiSlashCommands";
import type { KaiSlashSelection } from "./kaiTypes";
import type { KaiSlashCommand } from "@/services/kaiApi";
import { KaiAttachmentTokens } from "./KaiAttachmentTokens";
import {
  KaiEntityTagOptionBadge,
  KaiEntityTagOptionLead,
} from "./KaiEntityTagVisual";

/** Min characters of a free-text name before searching across all entities. */
const FREE_SEARCH_MIN_CHARS = 3;

/** Base classes shared by every inline mention pill in the editor. */
const PILL_BASE_CLASS =
  "kai-mention-pill mx-px whitespace-nowrap rounded-md px-1 py-px font-medium";

interface KaiComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (
    text: string,
    tags: KaiEntityTag[],
    attachmentIds: string[],
    slash: KaiSlashSelection | null,
  ) => void;
  onStop?: () => void;
  disabled?: boolean;
  isGenerating?: boolean;
  /** Agent is blocked on a clarification form — composer stays usable. */
  awaitingInput?: boolean;
  className?: string;
  placeholder?: string;
  /** Entities the user tagged for the next message (per-message, cleared on send). */
  entityTags?: KaiEntityTag[];
  onEntityTagsChange?: (tags: KaiEntityTag[]) => void;
  /** Slash commands available to the user (already RBAC/route-filtered). */
  slashCommands?: KaiSlashCommand[];
  /** Notified when the composer's active slash selection changes (or clears). */
  onSlashCommandChange?: (slash: KaiSlashSelection | null) => void;
  /** Files attached for the next message (per-message, cleared on send). */
  attachmentEntries?: AttachmentEntry[];
  /** Add newly picked files to the attachment queue. */
  onAddFiles?: (files: File[]) => void;
  /** Remove a queued attachment by index. */
  onRemoveAttachment?: (index: number) => void;
  /** True while an upload is in flight — sending is blocked. */
  attachmentsBlocking?: boolean;
}

interface MentionState {
  /** Text typed after the active `@`, up to the caret. */
  query: string;
  /** Chosen kind, or `null` while the kind picker is showing. */
  kind: KaiEntityTagKind | null;
}

function tagKey(tag: KaiEntityTag): string {
  if (tag.kind === "port") {
    return `${tag.kind}:${tag.value}:${tag.metadata?.port_role ?? "either"}`;
  }
  if (tag.kind === "booking_state") {
    return `${tag.kind}:${tag.metadata?.state_filter_mode ?? "state"}:${tag.value}`;
  }
  return `${tag.kind}:${tag.value}`;
}

function dedupeTags(tags: KaiEntityTag[]): KaiEntityTag[] {
  const seen = new Set<string>();
  const out: KaiEntityTag[] = [];
  for (const tag of tags) {
    const key = tagKey(tag);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(tag);
    }
  }
  return out;
}

function optionSearchLabels(tags: KaiEntityTag[]): string[] {
  return tags.flatMap((tag) => [tag.label, tag.value].filter(Boolean));
}

/** Keep keyboard-highlighted listbox options visible inside scrollable menus. */
function scrollHighlightedOption(container: HTMLDivElement | null) {
  if (!container) return;
  container
    .querySelector<HTMLElement>('[role="option"][aria-selected="true"]')
    ?.scrollIntoView({ block: "nearest" });
}

/** Build the non-editable inline pill DOM node for a tag. */
function createPillElement(
  tag: KaiEntityTag,
  opts: { showCarrierLogos: boolean; theme: "light" | "dark" },
): HTMLSpanElement {
  const span = document.createElement("span");
  span.contentEditable = "false";
  span.dataset.kind = tag.kind;
  span.dataset.value = tag.value;
  span.dataset.label = tag.label;
  if (tag.metadata) span.dataset.metadata = JSON.stringify(tag.metadata);
  const logoCarrier =
    tag.kind === "carrier"
      ? tag.value
      : tag.kind === "contract_ref"
        ? tag.metadata?.carrier
        : undefined;
  const showLogo = Boolean(logoCarrier) && opts.showCarrierLogos;
  span.className = cn(
    PILL_BASE_CLASS,
    entityPillClassName(tag.kind),
    showLogo && "inline-flex items-center gap-1",
  );
  span.title = `${entityTagKindDef(tag.kind).label} · click to remove`;
  span.appendChild(document.createTextNode("@"));
  if (showLogo && logoCarrier) {
    const logo = createCarrierLogoImg(logoCarrier, 14, opts.theme);
    if (logo) span.appendChild(logo);
  }
  span.appendChild(document.createTextNode(tag.label));
  return span;
}

/** Read the editor DOM into inline plain text + the ordered list of pills. */
function readEditor(el: HTMLElement): {
  text: string;
  tags: KaiEntityTag[];
  slash: KaiSlashSelection | null;
} {
  const tags: KaiEntityTag[] = [];
  el.querySelectorAll<HTMLElement>("[data-kind]").forEach((node) => {
    const kind = node.dataset.kind as KaiEntityTagKind | undefined;
    const value = node.dataset.value;
    const label = node.dataset.label;
    if (!kind || value == null || label == null) return;
    let metadata: KaiEntityTag["metadata"];
    if (node.dataset.metadata) {
      try {
        metadata = JSON.parse(node.dataset.metadata);
      } catch {
        metadata = undefined;
      }
    }
    tags.push({ kind, value, label, metadata });
  });
  // innerText preserves pill labels inline at their position in the sentence.
  const text = (el.innerText ?? "").replace(/\u00a0/g, " ");

  // At most one leading slash-command pill; its args are whatever the user
  // typed after it in the message text.
  let slash: KaiSlashSelection | null = null;
  const slashNode = el.querySelector<HTMLElement>("[data-slash-id]");
  const slashId = slashNode?.dataset.slashId;
  if (slashNode && slashId) {
    const label = slashNode.dataset.slashLabel ?? "";
    const marker = `/${label}`;
    const idx = text.indexOf(marker);
    const args = (idx === -1 ? text : text.slice(idx + marker.length)).trim();
    slash = { id: slashId, label, args: args || undefined };
  }

  return { text, tags, slash };
}

/** Build the non-editable inline pill DOM node for a slash command. */
function createSlashPillElement(command: KaiSlashCommand): HTMLSpanElement {
  const span = document.createElement("span");
  span.contentEditable = "false";
  span.dataset.slashId = command.id;
  span.dataset.slashLabel = command.label;
  span.className = `${PILL_BASE_CLASS} ${slashPillClassName()}`;
  span.title = "Slash command · click to remove";
  span.textContent = `/${command.label}`;
  return span;
}

export function KaiComposer({
  value,
  onChange,
  onSend,
  onStop,
  disabled,
  isGenerating,
  awaitingInput,
  className,
  placeholder = "Ask Kai…",
  entityTags = [],
  onEntityTagsChange,
  slashCommands = [],
  onSlashCommandChange,
  attachmentEntries = [],
  onAddFiles,
  onRemoveAttachment,
  attachmentsBlocking,
}: KaiComposerProps) {
  const { profile } = useUser();
  const userRole = profile?.role;
  const isClient = profile?.role === "Client";
  const { preferences } = useUserPreferences();
  const { theme } = useTheme();
  const tagKindDefs = useMemo(
    () => entityTagKindsForRole(userRole),
    [userRole],
  );
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastEmitted = useRef("");
  const [mention, setMention] = useState<MentionState | null>(null);
  /** Lane side applied to a `@port` pick (origin / destination / either). */
  const [portRole, setPortRole] = useState<KaiPortRole>("either");
  const [highlight, setHighlight] = useState(0);
  const [options, setOptions] = useState<KaiEntityTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [freeResults, setFreeResults] = useState<KaiEntityTag[]>([]);
  const [freeLoading, setFreeLoading] = useState(false);
  const [editorEmpty, setEditorEmpty] = useState(true);
  const [slash, setSlash] = useState<{ query: string } | null>(null);
  const [slashHighlight, setSlashHighlight] = useState(0);
  const requestRef = useRef(0);
  const freeRequestRef = useRef(0);
  /** Token we space-exited from, so refreshMention does not reopen it. */
  const exitedQueryRef = useRef<string | null>(null);
  /** True after Escape so keyup/refresh does not immediately reopen `/`. */
  const slashDismissedRef = useRef(false);
  const exitSlashMenuRef = useRef<() => void>(() => {});
  const mentionMenuRef = useRef<HTMLDivElement>(null);
  const slashMenuRef = useRef<HTMLDivElement>(null);

  const locked = Boolean(disabled) && !awaitingInput;
  const hasDraft = value.trim().length > 0;
  const canAttach = Boolean(onAddFiles);
  const doneAttachmentIds = attachmentEntries
    .filter((e) => e.status === "done" && e.attachment)
    .map((e) => e.attachment!.id);
  const hasSendableContent =
    hasDraft || entityTags.length > 0 || doneAttachmentIds.length > 0;
  const showStop = Boolean(isGenerating) && !(awaitingInput && hasDraft);
  const canTag = Boolean(onEntityTagsChange);
  const menuOpen = mention != null && canTag;
  const canSlash = Boolean(onSlashCommandChange);
  const slashOpen = slash != null && canSlash && !menuOpen;
  const slashResults = slashOpen
    ? filterSlashCommands(slashCommands, slash!.query)
    : [];
  const slashGroups = groupSlashCommands(slashResults);
  // Flat, display-ordered list for keyboard navigation (groups may merge
  // non-contiguous categories, so this matches the rendered order).
  const slashOrdered = slashGroups.flatMap((g) => g.commands);
  const kindPicker = menuOpen && mention?.kind == null;
  // In the kind picker, narrow the kinds by what the user typed after `@`
  // (e.g. `@agent` highlights Agent so Enter jumps straight into its list).
  const kindQuery = (mention?.query ?? "").trim().toLowerCase();
  const filteredKinds =
    kindPicker && kindQuery
      ? tagKindDefs.filter(
          (k) =>
            k.kind.includes(kindQuery) ||
            k.label.toLowerCase().includes(kindQuery),
        )
      : tagKindDefs;
  // When the text after `@` is a name (no kind keyword match) of 3+ letters,
  // search across every entity kind instead of the kind picker.
  const freeSearch =
    kindPicker &&
    filteredKinds.length === 0 &&
    kindQuery.length >= FREE_SEARCH_MIN_CHARS;

  /** iOS scrolls bottom inputs to the top of the viewport on focus — undo that. */
  const undoIosFocusScroll = useCallback(() => {
    if (!window.matchMedia(MOBILE_SHELL_MQ).matches) return;
    requestAnimationFrame(() => {
      if ((window.visualViewport?.offsetTop ?? 0) > 0) {
        window.scrollTo(0, 0);
      }
    });
  }, []);

  /** Focus before the browser's default scroll-into-view runs on mobile. */
  const handleEditorPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (locked) return;
      const el = editorRef.current;
      if (!el || document.activeElement === el) return;
      e.preventDefault();
      el.focus({ preventScroll: true });
      const range = document.caretRangeFromPoint?.(e.clientX, e.clientY);
      if (range) {
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    },
    [locked],
  );

  const handleEditorFocus = useCallback(() => {
    undoIosFocusScroll();
  }, [undoIosFocusScroll]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const { text, tags, slash: slashSel } = readEditor(el);
    lastEmitted.current = text;
    onChange(text);
    onEntityTagsChange?.(dedupeTags(tags));
    onSlashCommandChange?.(slashSel);
    setEditorEmpty(text.trim() === "" && tags.length === 0 && slashSel == null);
  }, [onChange, onEntityTagsChange, onSlashCommandChange]);

  // Reset the highlighted slash option whenever the query changes.
  useEffect(() => {
    setSlashHighlight(0);
  }, [slash?.query]);

  useEffect(() => {
    if (!menuOpen) return;
    const id = requestAnimationFrame(() => {
      scrollHighlightedOption(mentionMenuRef.current);
    });
    return () => cancelAnimationFrame(id);
  }, [highlight, menuOpen, loading, freeLoading, options, freeResults]);

  useEffect(() => {
    if (!slashOpen) return;
    const id = requestAnimationFrame(() => {
      scrollHighlightedOption(slashMenuRef.current);
    });
    return () => cancelAnimationFrame(id);
  }, [slashHighlight, slashOpen, slashOrdered.length]);

  // Escape closes the `/` menu even if focus left the editor.
  useEffect(() => {
    if (!slashOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      exitSlashMenuRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slashOpen]);

  // External resets (send / new chat) clear the value — mirror into the DOM.
  useEffect(() => {
    if (value !== "") return;
    const el = editorRef.current;
    if (el && el.textContent !== "") {
      el.innerHTML = "";
      lastEmitted.current = "";
      setEditorEmpty(true);
      setMention(null);
      setSlash(null);
      exitedQueryRef.current = null;
      slashDismissedRef.current = false;
    }
  }, [value]);

  // Async search once a kind is chosen.
  useEffect(() => {
    if (!menuOpen || !mention || mention.kind == null) {
      setOptions([]);
      return;
    }
    const kind = mention.kind;
    const query = mention.query.trimEnd();
    const id = ++requestRef.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchEntityTags(kind, query)
        .then((next) => {
          if (requestRef.current !== id) return;
          setOptions(next);
          setHighlight(0);
        })
        .catch(() => {
          if (requestRef.current === id) setOptions([]);
        })
        .finally(() => {
          if (requestRef.current === id) setLoading(false);
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [menuOpen, mention]);

  // Keep the highlight in range as the kind-picker query narrows the list.
  useEffect(() => {
    if (kindPicker) setHighlight(0);
  }, [kindPicker, kindQuery]);

  // Free-text cross-entity search when the query is a name, not a kind keyword.
  useEffect(() => {
    if (!freeSearch) {
      setFreeResults([]);
      setFreeLoading(false);
      return;
    }
    const query = kindQuery.trim();
    const id = ++freeRequestRef.current;
    setFreeLoading(true);
    const timer = window.setTimeout(() => {
      searchAllEntityTags(query, userRole)
        .then((next) => {
          if (freeRequestRef.current !== id) return;
          setFreeResults(next);
          setHighlight(0);
        })
        .catch(() => {
          if (freeRequestRef.current === id) setFreeResults([]);
        })
        .finally(() => {
          if (freeRequestRef.current === id) setFreeLoading(false);
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [freeSearch, kindQuery, userRole]);

  /** Recompute the active `@` token from the live caret position. */
  const refreshMention = useCallback(() => {
    if (!canTag) return;
    const sel = window.getSelection();
    const el = editorRef.current;
    if (!sel || sel.rangeCount === 0 || !el) {
      setMention(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (
      !range.collapsed ||
      node.nodeType !== Node.TEXT_NODE ||
      !el.contains(node)
    ) {
      setMention(null);
      return;
    }
    const upto = (node.textContent ?? "").slice(0, range.startOffset);
    const query = detectMentionQuery(upto);
    if (query == null) {
      setMention(null);
      return;
    }
    const exited = exitedQueryRef.current;
    if (exited && mentionStillExited(query, exited)) {
      setMention(null);
      return;
    }
    exitedQueryRef.current = null;
    setMention((prev) =>
      prev ? { ...prev, query } : { query, kind: null },
    );
  }, [canTag]);

  /**
   * Recompute the active `/command` token. A slash command is a single token at
   * the very start of the message, so it only opens when the caret sits in the
   * leading text node and no slash pill exists yet.
   */
  const refreshSlash = useCallback(() => {
    if (!canSlash) return;
    if (slashDismissedRef.current) {
      setSlash(null);
      return;
    }
    const sel = window.getSelection();
    const el = editorRef.current;
    if (!sel || sel.rangeCount === 0 || !el) {
      setSlash(null);
      return;
    }
    // Only one slash command per message.
    if (el.querySelector("[data-slash-id]")) {
      setSlash(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (
      !range.collapsed ||
      node.nodeType !== Node.TEXT_NODE ||
      !el.contains(node) ||
      el.firstChild !== node
    ) {
      setSlash(null);
      return;
    }
    const upto = (node.textContent ?? "").slice(0, range.startOffset);
    const query = detectSlashQuery(upto);
    if (query == null) {
      setSlash(null);
      return;
    }
    setSlash({ query });
  }, [canSlash]);

  const refreshMenus = useCallback(() => {
    refreshMention();
    refreshSlash();
  }, [refreshMention, refreshSlash]);

  function handleInput() {
    slashDismissedRef.current = false;
    emitChange();
    refreshMenus();
  }

  function closeMention() {
    setMention(null);
    requestRef.current += 1;
    freeRequestRef.current += 1;
  }

  function closeSlash() {
    setSlash(null);
  }

  /** Dismiss the `/` menu and strip the leading `/query` so the user fully exits. */
  function exitSlashMenu() {
    slashDismissedRef.current = true;
    const el = editorRef.current;
    const node =
      el?.firstChild && el.firstChild.nodeType === Node.TEXT_NODE
        ? el.firstChild
        : null;
    const text = node?.textContent ?? "";
    if (node && text.startsWith("/")) {
      const tokenLen = (text.match(/^\/[^\s]*/) ?? ["/"])[0].length;
      node.textContent = text.slice(tokenLen);
      const sel = window.getSelection();
      if (sel) {
        const collapsed = document.createRange();
        collapsed.setStart(node, 0);
        collapsed.collapse(true);
        sel.removeAllRanges();
        sel.addRange(collapsed);
      }
    }
    setSlash(null);
    emitChange();
    el?.focus({ preventScroll: true });
  }
  exitSlashMenuRef.current = exitSlashMenu;

  function exitMentionOnSpace(query: string) {
    exitedQueryRef.current = query.trimEnd();
    closeMention();
  }

  const selectedTagKeys = new Set(entityTags.map(tagKey));
  const unusedOptions = options.filter((opt) => !selectedTagKeys.has(tagKey(opt)));
  const unusedFreeResults = freeResults.filter(
    (opt) => !selectedTagKeys.has(tagKey(opt)),
  );
  const mentionOptionLabels = kindPicker
    ? freeSearch
      ? optionSearchLabels(unusedFreeResults)
      : filteredKinds.map((k) => k.label)
    : optionSearchLabels(unusedOptions);
  const mentionOptionsLoading = kindPicker
    ? freeSearch && freeLoading
    : loading;

  // After search settles, a trailing space with no spaced matches exits
  // (covers the case where we kept the mention open while results loaded).
  useEffect(() => {
    if (!menuOpen || !mention) return;
    if (mentionOptionsLoading) return;
    if (!/\s$/.test(mention.query)) return;
    if (dropdownAllowsSpace(mentionOptionLabels)) return;
    exitMentionOnSpace(mention.query);
  }, [
    menuOpen,
    mention,
    mentionOptionLabels,
    mentionOptionsLoading,
  ]);

  /** Replace the active `@query` run with an inline pill + trailing space. */
  function insertPill(tag: KaiEntityTag) {
    if (isClient && !isClientAllowedEntityTagKind(tag.kind)) return;
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0 || !mention) return;
    // Stamp the chosen lane side onto a port pick before it is frozen.
    const resolved: KaiEntityTag =
      tag.kind === "port"
        ? { ...tag, metadata: { ...tag.metadata, port_role: portRole } }
        : tag;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !el.contains(node)) return;
    const caret = range.startOffset;
    const start = Math.max(caret - 1 - mention.query.length, 0);
    const del = document.createRange();
    del.setStart(node, start);
    del.setEnd(node, caret);
    del.deleteContents();
    const pill = createPillElement(resolved, {
      showCarrierLogos: preferences.showCarrierLogos,
      theme,
    });
    del.insertNode(pill);
    const space = document.createTextNode("\u00a0");
    pill.after(space);
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    setMention(null);
    emitChange();
    el.focus({ preventScroll: true });
  }

  /** Replace the leading `/query` run with a slash pill + trailing space. */
  function insertSlashPill(command: KaiSlashCommand) {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0 || !slash) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !el.contains(node)) return;
    const caret = range.startOffset;
    // Remove the leading `/` plus the typed query (start of the node → caret).
    const start = Math.max(caret - 1 - slash.query.length, 0);
    const del = document.createRange();
    del.setStart(node, start);
    del.setEnd(node, caret);
    del.deleteContents();
    const pill = createSlashPillElement(command);
    del.insertNode(pill);
    const space = document.createTextNode("\u00a0");
    pill.after(space);
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    setSlash(null);
    emitChange();
    el.focus({ preventScroll: true });
  }

  /** Pick a kind: strip the typed kind name so the value list opens empty. */
  function chooseKind(kind: KaiEntityTagKind) {
    if (isClient && !isClientAllowedEntityTagKind(kind)) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && mention && mention.query.length > 0) {
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const caret = range.startOffset;
        const from = Math.max(caret - mention.query.length, 0);
        const del = document.createRange();
        del.setStart(node, from);
        del.setEnd(node, caret);
        del.deleteContents();
        const collapsed = document.createRange();
        collapsed.setStart(node, from);
        collapsed.collapse(true);
        sel.removeAllRanges();
        sel.addRange(collapsed);
      }
    }
    if (kind === "port") setPortRole("either");
    setMention({ query: "", kind });
    setHighlight(0);
    emitChange();
    editorRef.current?.focus({ preventScroll: true });
  }

  /** Click an inline pill (entity or slash) to remove it. */
  function handleEditorClick(e: MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    const pill = target.closest<HTMLElement>("[data-kind], [data-slash-id]");
    if (pill && editorRef.current?.contains(pill)) {
      pill.remove();
      emitChange();
      editorRef.current?.focus({ preventScroll: true });
    }
  }

  function submit() {
    if (locked || attachmentsBlocking) return;
    const el = editorRef.current;
    const { text, tags, slash: slashSel } = el
      ? readEditor(el)
      : { text: value, tags: entityTags, slash: null };
    const trimmed = text.trim();
    if (
      !trimmed &&
      tags.length === 0 &&
      doneAttachmentIds.length === 0 &&
      slashSel == null
    )
      return;
    onSend(trimmed, dedupeTags(tags), doneAttachmentIds, slashSel);
    if (el) el.innerHTML = "";
    lastEmitted.current = "";
    setEditorEmpty(true);
    onChange("");
    onEntityTagsChange?.([]);
    onSlashCommandChange?.(null);
    closeMention();
    closeSlash();
    el?.focus({ preventScroll: true });
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) onAddFiles?.(files);
    // Reset so re-picking the same file fires another change event.
    e.target.value = "";
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (slashOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashHighlight((i) =>
          Math.min(i + 1, Math.max(slashOrdered.length - 1, 0)),
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashHighlight((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        exitSlashMenu();
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        // While the `/` menu is open, Enter/Tab selects — never sends.
        e.preventDefault();
        const chosen = slashOrdered[slashHighlight];
        if (chosen) insertSlashPill(chosen);
        return;
      }
    }
    if (menuOpen) {
      if (e.key === " " || e.key === "Spacebar") {
        const query = mention?.query ?? "";
        if (
          !shouldKeepMentionOnSpace(
            query,
            mentionOptionLabels,
            mentionOptionsLoading,
          )
        ) {
          exitMentionOnSpace(query);
          return;
        }
      }
      const list = kindPicker
        ? freeSearch
          ? unusedFreeResults
          : filteredKinds
        : unusedOptions;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((i) => Math.min(i + 1, Math.max(list.length - 1, 0)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
        return;
      }
      if (e.key === "Enter") {
        // While the `@` menu is open, Enter selects — never sends.
        e.preventDefault();
        if (freeSearch) {
          if (unusedFreeResults[highlight]) insertPill(unusedFreeResults[highlight]);
        } else if (kindPicker) {
          if (filteredKinds[highlight]) chooseKind(filteredKinds[highlight].kind);
        } else if (unusedOptions[highlight]) {
          insertPill(unusedOptions[highlight]);
        }
        return;
      }
    }
    // Enter sends; Shift+Enter falls through to insert a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "glass-panel-strong clay-strong relative rounded-2xl p-2 touch-manipulation",
        className,
      )}
    >
      {menuOpen && (
        <div
          ref={mentionMenuRef}
          className={cn(
            glassMenuPanelClassName,
            "absolute bottom-full left-0 z-20 mb-2 max-h-64 w-full",
          )}
        >
          {kindPicker ? (
            freeSearch ? (
              <>
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Matches for “{kindQuery}”
                </p>
                {freeLoading && unusedFreeResults.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                    <Loader2 className="size-3 animate-spin" />
                    Searching…
                  </div>
                ) : unusedFreeResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    No entities found
                  </p>
                ) : (
                  unusedFreeResults.map((opt, index) => {
                    return (
                      <button
                        key={`${tagKey(opt)}-${index}`}
                        type="button"
                        role="option"
                        aria-selected={highlight === index}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left",
                          highlight === index
                            ? "bg-primary/12 text-foreground"
                            : "text-foreground/90 hover:bg-accent/50",
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setHighlight(index)}
                        onClick={() => insertPill(opt)}
                      >
                        <KaiEntityTagOptionLead tag={opt} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="text-sm">{opt.label}</span>
                          <span className="text-[11px] text-muted-foreground">
                            {entityTagKindDef(opt.kind).label}
                          </span>
                        </span>
                        <KaiEntityTagOptionBadge tag={opt} />
                      </button>
                    );
                  })
                )}
              </>
            ) : filteredKinds.length > 0 ? (
              <>
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Tag an entity
                </p>
                {filteredKinds.map((def, index) => {
                  const Icon = def.icon;
                  return (
                    <button
                      key={def.kind}
                      type="button"
                      role="option"
                      aria-selected={highlight === index}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left",
                        highlight === index
                          ? "bg-primary/12 text-foreground"
                          : "text-foreground/90 hover:bg-accent/50",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => chooseKind(def.kind)}
                    >
                      <Icon className="size-4 shrink-0 text-muted-foreground" />
                      <span className="flex flex-col">
                        <span className="text-sm">{def.label}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {def.hint}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Type at least {FREE_SEARCH_MIN_CHARS} letters to search by name
              </p>
            )
          ) : (
            <>
              <p className="flex items-center gap-1.5 px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {mention?.kind ? entityTagKindDef(mention.kind).label : ""}
              </p>
              {mention?.kind === "port" && (
                <div className="flex items-center gap-1 px-3 pb-1.5">
                  {KAI_PORT_ROLE_OPTIONS.map((opt) => (
                    <button
                      key={opt.role}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setPortRole(opt.role);
                        editorRef.current?.focus({ preventScroll: true });
                      }}
                      className={cn(
                        "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors",
                        portRole === opt.role
                          ? "bg-cyan-500/20 text-cyan-700 dark:text-cyan-300"
                          : "text-muted-foreground hover:bg-accent/50",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {loading && unusedOptions.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Searching…
                </div>
              ) : unusedOptions.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  {mention?.kind
                    ? `No ${entityTagKindDef(mention.kind).label.toLowerCase()}s found`
                    : "No matches"}
                </p>
              ) : (
                unusedOptions.map((opt, index) => {
                  const subtitle = entityTagSubtitle(opt);
                  return (
                    <button
                      key={`${tagKey(opt)}-${index}`}
                      type="button"
                      role="option"
                      aria-selected={highlight === index}
                      className={cn(
                        "flex w-full items-start gap-2.5 rounded-lg px-3 py-1.5 text-left",
                        highlight === index
                          ? "bg-primary/12 text-foreground"
                          : "text-foreground/90 hover:bg-accent/50",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => insertPill(opt)}
                    >
                      <KaiEntityTagOptionLead tag={opt} className="mt-0.5" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm">{opt.label}</span>
                        {subtitle && (
                          <span className="text-[11px] text-muted-foreground">
                            {subtitle}
                          </span>
                        )}
                      </span>
                      <KaiEntityTagOptionBadge tag={opt} />
                    </button>
                  );
                })
              )}
            </>
          )}
        </div>
      )}

      {slashOpen && (
        <div
          ref={slashMenuRef}
          className={cn(
            glassMenuPanelClassName,
            "absolute bottom-full left-0 z-20 mb-2 max-h-64 w-full",
          )}
        >
          {slashOrdered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No commands match “/{slash?.query}”
            </p>
          ) : (
            slashGroups.map((group) => (
              <div key={group.category}>
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {group.category}
                </p>
                {group.commands.map((command) => {
                  const index = slashOrdered.indexOf(command);
                  const active = index === slashHighlight;
                  return (
                    <button
                      key={command.id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={cn(
                        "flex w-full min-w-0 flex-col rounded-lg px-3 py-1.5 text-left",
                        active
                          ? "bg-primary/12 text-foreground"
                          : "text-foreground/90 hover:bg-accent/50",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setSlashHighlight(index)}
                      onClick={() => insertSlashPill(command)}
                    >
                      <span className="truncate text-sm">/{command.label}</span>
                      <span className="truncate text-[11px] text-muted-foreground">
                        {command.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {canAttach && (
        <KaiAttachmentTokens
          entries={attachmentEntries}
          onRemove={(index) => onRemoveAttachment?.(index)}
        />
      )}

      <div className="flex items-end gap-2">
        {canAttach && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={KAI_ACCEPT}
            className="hidden"
            onChange={handleFileInputChange}
          />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 rounded-xl text-muted-foreground"
          aria-label="Attach file"
          disabled={locked || !canAttach}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="size-4" />
        </Button>
        <div className="relative min-h-9 flex-1">
          <div
            ref={editorRef}
            role="textbox"
            aria-multiline="true"
            aria-label="Message Kai"
            contentEditable={!locked}
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onKeyUp={refreshMenus}
            onMouseUp={refreshMenus}
            onClick={handleEditorClick}
            onPointerDown={handleEditorPointerDown}
            onFocus={handleEditorFocus}
            className={cn(
              "max-h-32 min-h-9 w-full overflow-y-auto scroll-m-0 whitespace-pre-wrap break-words py-2 text-base leading-relaxed outline-none [text-size-adjust:100%] md:text-sm",
              locked && "opacity-50",
            )}
          />
          {editorEmpty && (
            <>
              <span className="pointer-events-none absolute inset-x-0 top-2 truncate pr-1 text-base leading-relaxed text-muted-foreground md:hidden">
                {awaitingInput ? "Reply…" : "Ask Kai…"}
              </span>
              <span className="pointer-events-none absolute inset-x-0 top-2 hidden select-none text-sm leading-relaxed text-muted-foreground md:block">
                {awaitingInput ? "Or reply in your own words…" : placeholder}
              </span>
            </>
          )}
        </div>
        {showStop ? (
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="size-9 shrink-0 rounded-xl"
            aria-label="Stop generating"
            onClick={onStop}
          >
            <Square className="size-3.5 fill-current" />
          </Button>
        ) : (
          <Button
            type="submit"
            size="icon"
            className="size-9 shrink-0 rounded-xl"
            aria-label="Send message"
            disabled={locked || !hasSendableContent || Boolean(attachmentsBlocking)}
          >
            <ArrowUp className="size-4" />
          </Button>
        )}
      </div>
    </form>
  );
}
